import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useContext,
} from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { Box, Alert } from "@mui/material";
import {
  AuthContext,
  usePosition,
  useZip,
  FALLBACK_POSITION,
} from "@healthdesk/shared-hooks";
import MapWrapper from "./MapWrapper";
import MapStackedZoomControls from "./MapStackedZoomControls";
import AdCarousel from "./AdCarousel";
import MapUpdater from "./MapUpdater";
import MapEventHandler from "./MapEventHandler";
import { useLocationData } from "./useLocationData";
import { CITY_COORDINATES } from "./constants";
import Marker from "./Marker";
import Loading from "../Loading";
import Modal from "./Modal";
import Header from "./Header";
import Welcome from "./Welcome";
import PhoneAuthDialog from "../auth/PhoneAuthDialog";
import AnimatedBeamModal from "./AnimatedBeamModal";
import { useNavigate } from "react-router-dom";
import { MAP_LAYERS } from "../../config/mapLayers";
import { useLayerData } from "./useLayerData";
import { useMapOverlayMenuLayers } from "./useMapOverlayMenuLayers";
import LayerMarker from "./LayerMarker";
import LayerMarkerDetailModal from "./LayerMarkerDetailModal";
import { getMapTilerStreetTileUrl } from "../../utils/mapTilerStreetTileUrl";
import { getCapabilityLabel } from "services/facilityMatch";
import { tokens } from "@healthdesk/shared-utils";

/** Passed to L.markerClusterGroup for GeoFirestore facility markers (large counts). */
const CAREMAP_FACILITY_MARKER_CLUSTER_PROPS = {
  chunkedLoading: true,
};

const Map = ({
  initialFilter = { facility: "all", rating: 0, showRestrictedAccess: false },
  HeaderComponent = Header,
  MapUpdaterComponent = MapUpdater,
  showModal = true,
  sx = {},
  showAds = true,
  showWelcomeDialog = true,
  menuInBar = false,
  /** `stacked`: white header + ad strip above map (CareMap /); tiles match header width. `overlay`: legacy header over map. */
  layout = "overlay",
  /** When false, hide overlay layer dropdown and static layer markers (e.g. chat MiniMap). */
  showLayerOverlay = true,
  /** /scmap: location ids to highlight as capability matches (default none). */
  highlightFacilityIds = [],
  /** /scmap: map of locationId → match metadata for marker hues/tooltips (default none). */
  matchDataById = {},
  /** /scmap: diagnostic need keys for the informational banner above the map. */
  diagnosticNeeds = [],
  /** /scmap: session-level diagnostic need keys for modal triage context. */
  patientNeeds = [],
  // Map defaults are viewport-filling for / and /map; embedded=true opts into parent-constrained layout
  // so card-based surfaces (like /scmap post-conversation) do not escape their container.
  /** When true (SCMap embed), government/restricted facilities stay hidden regardless of filter. */
  forceHideRestrictedAccess = false,
  /** /scmap: hide EDs with CMS OP-18b median discharge time over 2 hours. */
  hideLongWaitEds = false,
  embedded = false,
}) => {
  const stacked = layout === "stacked";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const { latitude, longitude, error, coords, setCoords, permissionDenied } =
    usePosition();
  const { setCustomLocation, allListings, featuredListings, listingsLoaded } =
    useZip();
  const [filter, setFilter] = useState(initialFilter);
  const effectiveFilter = useMemo(
    () =>
      forceHideRestrictedAccess
        ? { ...filter, showRestrictedAccess: false }
        : filter,
    [filter, forceHideRestrictedAccess]
  );
  const [searchLoaded, setSearchLoaded] = useState(true);
  const [modalOpen, setModalOpen] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [beamModalOpen, setBeamModalOpen] = useState(false);
  const [selectedLayer, setSelectedLayer] = useState("");
  const [layerDetailMarker, setLayerDetailMarker] = useState(null);
  const mapRef = useRef();
  const { userData } = useContext(AuthContext);

  const isCapabilityDebugRole = useMemo(() => {
    const role = String(userData?.role || "");
    return (
      role === "chartmind-admin" ||
      role === "chartmind-kijabe" ||
      role === "system-medical-admin" ||
      role === "llm-manager-global"
    );
  }, [userData?.role]);

  const includeUnverifiedFacilities = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (!isCapabilityDebugRole) return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("includeUnverified") === "1";
  }, [isCapabilityDebugRole]);

  const getLocations = useLocationData(setData, setSearchLoaded, setLoading, {
    includeUnverifiedFacilities,
  });

  const overlayMenuLayers = useMapOverlayMenuLayers();

  useEffect(() => {
    if (
      selectedLayer &&
      !overlayMenuLayers.some((l) => l.categoryId === selectedLayer)
    ) {
      setSelectedLayer("");
    }
  }, [selectedLayer, overlayMenuLayers]);

  const layerCategoryId = showLayerOverlay && selectedLayer ? selectedLayer : null;
  const { layerMarkers, layerMeta, layerLoading, layerError } =
    useLayerData(layerCategoryId);

  const overlayMarkerColor = useMemo(() => {
    const cfg = MAP_LAYERS.find((l) => l.categoryId === selectedLayer);
    return (
      layerMeta?.markerColor ||
      cfg?.markerColor ||
      tokens.mapMarker.layerCategoryFallback
    );
  }, [selectedLayer, layerMeta]);

  const updateMap = useCallback(
    (map, newCoords, zoomLevel) => {
      if (!map || !map.getContainer || !map.getContainer()) {
        console.warn("Map not ready, skipping update");
        return;
      }
      setSearchLoaded(false);
      try {
        map.setView([newCoords.lat, newCoords.lng], zoomLevel || map.getZoom(), {
          animate: true,
          duration: 1,
        });
        getLocations(newCoords, zoomLevel);
      } catch (err) {
        console.error("Error updating map:", err);
      }
    },
    [getLocations]
  );

  const preventMapInteraction = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleLocationClick = useCallback(
    (coordinates) => {
      if (mapRef.current && coordinates) {
        const map = mapRef.current;
        updateMap(map, coordinates, 15);
      }
    },
    [updateMap]
  );

  const handleAuthSuccess = useCallback(() => {
    setAuthModalOpen(false);
    navigate("/dashboard");
  }, [navigate]);

  useEffect(() => {
    // Embedded maps (e.g. CareMap 2 on /scmap) must not inherit URL path as a facility group filter.
    if (embedded) return;

    const path = window.location.href.split("/").pop();
    if (CITY_COORDINATES[path]) {
      setCoords(CITY_COORDINATES[path]);
    } else if (path === "urgent") {
      setFilter((f) => ({ ...f, facility: "clinic" }));
    } else if (path === "emergency") {
      setFilter((f) => ({ ...f, facility: "emergency" }));
    } else if (path && path !== "map") {
      setFilter((f) => ({ ...f, group: path }));
    }
  }, [embedded, setCoords, setFilter]);

  useEffect(() => {
    if (latitude) {
      getLocations({ lat: latitude, lng: longitude });
    }
  }, [latitude, longitude, getLocations]);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 12000);
    return () => clearTimeout(t);
  }, []);

  const isFallbackLocation =
    permissionDenied &&
    coords.lat === FALLBACK_POSITION.latitude &&
    coords.lng === FALLBACK_POSITION.longitude;

  useEffect(() => {
    if (isFallbackLocation && showWelcomeDialog) {
      setShowWelcome(true);
    }
  }, [isFallbackLocation, showWelcomeDialog]);

  if (loading && !isFallbackLocation) return <Loading page />;

  const modalMatchData = modalOpen?.id
    ? matchDataById[modalOpen.id] ?? (
        patientNeeds?.length > 0
          ? { patientNeeds, matchScore: null, requiredCapabilitiesCount: patientNeeds.length }
          : undefined
      )
    : undefined;
  const markerMatchDataById = matchDataById || {};

  const headerProps = {
    data,
    filter,
    setFilter,
    setCoords,
    setCustomLocation,
    updateMap,
    searchLoaded,
    ads: featuredListings,
    onLocationClick: handleLocationClick,
    onAuthClick: () => setAuthModalOpen(true),
    onOpenBeamModal: () => setBeamModalOpen(true),
    onClick: preventMapInteraction,
    onDoubleClick: preventMapInteraction,
    onWheel: preventMapInteraction,
    menuInBar,
    showAds,
    adListings: allListings,
    listingsLoaded,
    showLayerOverlay,
    selectedLayer,
    setSelectedLayer,
    layerLoading,
    layerMeta,
    layerError,
    overlayMenuLayers,
    showRestrictedAccessToggle: !forceHideRestrictedAccess,
  };

  return (
    <MapWrapper sx={sx} embedded={embedded}>
      {diagnosticNeeds.length > 0 ? (
        <Alert
          severity="info"
          icon={false}
          sx={{ flexShrink: 0, py: 0.75, px: 1.5, borderRadius: 0 }}
        >
          Based on your symptoms, you may need:{" "}
          {diagnosticNeeds.map(getCapabilityLabel).join(", ")}
        </Alert>
      ) : null}
      {stacked && (
        <>
          <Box sx={{ flexShrink: 0, width: "100%", zIndex: 1000 }}>
            <HeaderComponent
              {...headerProps}
              stacked
              adCarouselInHeader={false}
              hideMapZoomControls
            />
          </Box>
        </>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <MapContainer
          ref={mapRef}
          attributionControl={false}
          style={{
            height: "100%",
            width: "100%",
          }}
          center={[coords.lat, coords.lng]}
          zoom={13}
          zoomControl={false}
          animate={true}
          doubleClickZoom={false}
        >
          {!stacked && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
              }}
            >
              <HeaderComponent {...headerProps} />
            </Box>
          )}
          {stacked && <MapStackedZoomControls />}
          {includeUnverifiedFacilities ? (
            <Box
              sx={{
                position: "absolute",
                left: 8,
                bottom: 8,
                zIndex: 900,
                backgroundColor: "warning.light",
                color: "warning.contrastText",
                px: 1.25,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            >
              Admin debug: showing unverified facilities
            </Box>
          ) : null}

          {/* MapTiler key from env (still public in bundle; single place to rotate). */}
          <TileLayer url={getMapTilerStreetTileUrl()} />
          <MarkerClusterGroup {...CAREMAP_FACILITY_MARKER_CLUSTER_PROPS}>
            {data.map((d) => (
              <Marker
                key={d.id}
                data={d}
                filter={effectiveFilter}
                setModalOpen={setModalOpen}
                setModalVisible={setModalVisible}
                highlightFacilityIds={highlightFacilityIds}
                matchDataById={markerMatchDataById}
                includeUnverifiedFacilities={includeUnverifiedFacilities}
                forceHideRestrictedAccess={forceHideRestrictedAccess}
                hideLongWaitEds={hideLongWaitEds}
              />
            ))}
          </MarkerClusterGroup>
          {showLayerOverlay &&
            layerMarkers.map((m) => (
              <LayerMarker
                key={m.id}
                marker={m}
                color={overlayMarkerColor}
                onOpenDetail={setLayerDetailMarker}
              />
            ))}
          <MapUpdaterComponent coords={coords} updateMap={updateMap} />
          <MapEventHandler getLocations={getLocations} />
        </MapContainer>
        {stacked && showAds && (
          <AdCarousel
            ads={allListings}
            listingsLoaded={listingsLoaded}
            onLocationClick={handleLocationClick}
            setFilter={setFilter}
            placement="mapBottom"
          />
        )}
      </Box>

      {showModal && (
        <Modal
          modalOpen={modalOpen}
          modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          matchData={modalMatchData}
          isEmbedded={embedded}
        />
      )}

      <LayerMarkerDetailModal
        open={Boolean(layerDetailMarker)}
        marker={layerDetailMarker}
        layerMeta={layerMeta}
        categoryId={layerCategoryId}
        onClose={() => setLayerDetailMarker(null)}
      />

      <Welcome
        open={showWelcome}
        close={() => setShowWelcome(false)}
        setCoords={setCoords}
        setCustomLocation={setCustomLocation}
      />

      <PhoneAuthDialog
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={handleAuthSuccess}
      />

      <AnimatedBeamModal
        open={beamModalOpen}
        onClose={() => setBeamModalOpen(false)}
      />
    </MapWrapper>
  );
};

export default Map;
